import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

const ThemeContext = createContext(null);

const STORAGE_KEY = 'operis-theme';
const LEGACY_KEYS = ['themePreferences', 'theme', 'landing-theme'];

const DEFAULT_PREFS = {
  darkMode: false,
  accentColor: '#f59e0b',
  fontSize: 'md',
  layoutDensity: 'normal',
  borderRadius: 'lg',
};

/* ─── Preset color palettes ─── */
export const ACCENT_PRESETS = [
  { name: 'Amber', hex: '#f59e0b', ring: '#fbbf24' },
  { name: 'Emerald', hex: '#10b981', ring: '#34d399' },
  { name: 'Indigo', hex: '#6366f1', ring: '#818cf8' },
  { name: 'Blue', hex: '#3b82f6', ring: '#60a5fa' },
  { name: 'Purple', hex: '#8b5cf6', ring: '#a78bfa' },
  { name: 'Cyan', hex: '#06b6d4', ring: '#22d3ee' },
  { name: 'Teal', hex: '#14b8a6', ring: '#2dd4bf' },
  { name: 'Orange', hex: '#f97316', ring: '#fb923c' },
  { name: 'Slate', hex: '#64748b', ring: '#94a3b8' },
  { name: 'Rose', hex: '#f43f5e', ring: '#fb7185' },
];

export const FONT_SIZES = [
  { key: 'sm', label: 'Small', basePx: 14 },
  { key: 'md', label: 'Medium', basePx: 16 },
  { key: 'lg', label: 'Large', basePx: 18 },
];

export const LAYOUT_DENSITIES = [
  { key: 'compact', label: 'Compact', spacingScale: 0.8 },
  { key: 'normal', label: 'Normal', spacingScale: 1.0 },
  { key: 'comfortable', label: 'Comfortable', spacingScale: 1.2 },
];

export const BORDER_RADII = [
  { key: 'none', label: 'Sharp', px: 0 },
  { key: 'sm', label: 'Slight', px: 4 },
  { key: 'md', label: 'Medium', px: 8 },
  { key: 'lg', label: 'Rounded', px: 12 },
  { key: 'xl', label: 'Pill', px: 16 },
];

function hexToHsl(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h;
  let s;
  const l = (max + min) / 2;

  if (max === min) {
    h = 0;
    s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      default:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
}

function hslToHex(h, s, l) {
  s /= 100;
  l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

function generateShades(hex) {
  const [h, s] = hexToHsl(hex);
  return {
    50: hslToHex(h, Math.min(s + 10, 100), 97),
    100: hslToHex(h, Math.min(s + 5, 100), 93),
    200: hslToHex(h, s, 85),
    300: hslToHex(h, s, 74),
    400: hslToHex(h, s, 62),
    500: hex,
    600: hslToHex(h, s, 42),
    700: hslToHex(h, s, 34),
    800: hslToHex(h, s, 26),
    900: hslToHex(h, s, 18),
    950: hslToHex(h, s, 12),
  };
}

function hexToRgbString(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r} ${g} ${b}`;
}

function getSystemDarkMode() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}

function readLegacyTheme() {
  if (typeof window === 'undefined') return null;

  for (const key of LEGACY_KEYS) {
    const value = window.localStorage.getItem(key);
    if (!value) continue;

    if (key === 'theme' || key === 'landing-theme') {
      return { ...DEFAULT_PREFS, darkMode: value === 'dark' };
    }

    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed.darkMode === 'boolean') {
        return { ...DEFAULT_PREFS, ...parsed, darkMode: parsed.darkMode };
      }
    } catch {
      // Ignore malformed legacy theme payloads and fall back below.
    }
  }

  return null;
}

function readStoredPrefs() {
  if (typeof window === 'undefined') {
    return { ...DEFAULT_PREFS, darkMode: false };
  }

  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed && typeof parsed.darkMode === 'boolean') {
        return { ...DEFAULT_PREFS, ...parsed, darkMode: parsed.darkMode };
      }
    }
  } catch {
    // Ignore corrupted storage and recover from legacy/system defaults.
  }

  const legacy = readLegacyTheme();
  if (legacy) return legacy;

  return { ...DEFAULT_PREFS, darkMode: getSystemDarkMode() };
}

function applyThemeToDOM(prefs) {
  if (typeof document === 'undefined') return;

  const mode = prefs.darkMode ? 'dark' : 'light';
  const root = document.documentElement;

  // Keep the DOM in sync with the theme state before the rest of the UI paints.
  root.dataset.theme = mode;
  root.classList.toggle('dark', prefs.darkMode);
  root.style.colorScheme = mode;
  if (document.body) {
    document.body.dataset.theme = mode;
  }

  const shades = generateShades(prefs.accentColor);
  Object.entries(shades).forEach(([shade, color]) => {
    root.style.setProperty(`--accent-${shade}`, color);
    root.style.setProperty(`--accent-${shade}-rgb`, hexToRgbString(color));
  });
  root.style.setProperty('--accent', prefs.accentColor);
  root.style.setProperty('--accent-rgb', hexToRgbString(prefs.accentColor));

  const fontConfig = FONT_SIZES.find((f) => f.key === prefs.fontSize) || FONT_SIZES[1];
  root.style.setProperty('--base-font-size', `${fontConfig.basePx}px`);
  root.style.fontSize = `${fontConfig.basePx}px`;

  const densityConfig = LAYOUT_DENSITIES.find((d) => d.key === prefs.layoutDensity) || LAYOUT_DENSITIES[1];
  root.style.setProperty('--spacing-scale', densityConfig.spacingScale);

  const radiusConfig = BORDER_RADII.find((r) => r.key === prefs.borderRadius) || BORDER_RADII[3];
  root.style.setProperty('--radius-base', `${radiusConfig.px}px`);
  root.style.setProperty('--radius-lg', `${radiusConfig.px + 4}px`);
  root.style.setProperty('--radius-xl', `${radiusConfig.px + 8}px`);
}

function persistPrefs(prefs) {
  if (typeof window === 'undefined') return;

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  LEGACY_KEYS.forEach((key) => window.localStorage.removeItem(key));
}

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
};

export const ThemeProvider = ({ children }) => {
  const [prefs, setPrefs] = useState(readStoredPrefs);

  useLayoutEffect(() => {
    applyThemeToDOM(prefs);
    persistPrefs(prefs);
  }, [prefs]);

  const updatePref = useCallback((key, value) => {
    setPrefs((prev) => ({ ...prev, [key]: value }));
  }, []);

  const setThemeMode = useCallback((mode) => {
    setPrefs((prev) => ({ ...prev, darkMode: mode === 'dark' }));
  }, []);

  const toggleTheme = useCallback(() => {
    setPrefs((prev) => ({ ...prev, darkMode: !prev.darkMode }));
  }, []);

  const resetTheme = useCallback(() => {
    setPrefs({ ...DEFAULT_PREFS });
  }, []);

  const value = useMemo(() => ({
    darkMode: prefs.darkMode,
    themeMode: prefs.darkMode ? 'dark' : 'light',
    setThemeMode,
    toggleTheme,
    prefs,
    updatePref,
    resetTheme,
    ACCENT_PRESETS,
    FONT_SIZES,
    LAYOUT_DENSITIES,
    BORDER_RADII,
  }), [prefs, resetTheme, setThemeMode, toggleTheme, updatePref]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
